/* ============================================================
   The rows and tiles every list is made of — the mobile app's, in HTML:
   the white card group with hairline separators, the 30px check ring, the
   habit tile with its 64px ring, the cadence badge, the pin marker. A row
   opens its action sheet on click; the ring completes directly (a desktop
   affordance a phone can't offer safely). Everything arrives in a quick
   stagger (`index` → --i) and answers the pointer: tiles lift, rows warm,
   rings glow in their hue.
   ============================================================ */
import React, { useState } from 'react';
import { isDueOn } from '@habishine/shared';
import { formatTime, type DayCheckin, type Habit, type HueName, type Task, todayStr } from '@habishine/client-core';

import { hueVar } from '../theme/hues';
import { Burst } from './Burst';
import { Icon, type IconName } from './Icon';
import { HabitRing, Ring, RingButton } from './Ring';

export function CardGroup({ children, className = '', index }: { children: React.ReactNode; className?: string; index?: number }) {
  return <div className={`surface overflow-hidden ${index != null ? 'rise-in' : ''} ${className}`} style={index != null ? { ['--i' as string]: index } : undefined}>{children}</div>;
}

/* a section's label: the heading face, with the count in a small pill
   beside it — a label with a number, not a form legend */
export function SectionHead({ children, right, count }: { children: React.ReactNode; right?: React.ReactNode; count?: number }) {
  return (
    <div className="mb-2.5 mt-7 flex items-center justify-between px-1">
      <h2 className="font-head flex items-center gap-2 text-ink">
        {children}
        {count != null && count > 0 ? <span className="num rounded-full bg-card-sunk px-1.5 py-px font-sans text-[11px] font-bold tracking-normal text-tertiary">{count}</span> : null}
      </h2>
      {right}
    </div>
  );
}

/* ---------- habit cadence badge ---------- */
export function HabitBadge({ habit, className = '' }: { habit: Habit; className?: string }) {
  if (habit.period) {
    return (
      <span className={`num text-[11px] text-tertiary ${className}`} title={`${habit.period.done} of ${habit.period.target} this week`}>
        <span className="text-[13px] font-bold text-meta">{habit.period.done}</span>/{habit.period.target}
      </span>
    );
  }
  return habit.streak > 0 ? (
    <span className={`text-[13px] text-meta ${habit.atRisk ? 'opacity-70 grayscale' : ''} ${className}`}>
      🔥 <span className="num text-[11px] text-tertiary">{habit.streak}</span>
    </span>
  ) : (
    <span className={`text-[13px] text-tertiary opacity-60 ${className}`}>✦</span>
  );
}

export function PinBadge({ small }: { small?: boolean }) {
  return (
    <span className={`flex items-center justify-center rounded-full bg-frog-soft text-frog ${small ? 'h-5 w-5' : 'h-6 w-6'}`} aria-label="Pinned — do this first">
      <Icon name="pin" size={small ? 11 : 13} filled />
    </span>
  );
}

/* ---------- habit tile (the grid) ---------- */
/** The last seven days as dots — kept, missed, rest, or still ahead. A read of
   consistency that degrades gracefully: a miss is one hollow dot, never a
   broken counter. */
export function WeekDots({ habit, days, weekDays }: { habit: Habit; days: Record<string, DayCheckin> | undefined; weekDays: string[] }) {
  const today = todayStr();
  return (
    <span className="flex items-center gap-[5px]" aria-label="This week">
      {weekDays.map((d) => {
        const future = d > today;
        const rest = !future && !isDueOn(habit.schedule, d, habit.startsOn);
        const kept = !!days?.[d]?.completed;
        const partial = !kept && (days?.[d]?.progress ?? 0) > 0;
        return (
          <span
            key={d}
            className="block h-[6px] w-[6px] rounded-full transition-colors"
            style={
              kept ? { background: hueVar(habit.hue, 'c') }
              : partial ? { background: hueVar(habit.hue, 'c'), opacity: 0.45 }
              : future || rest ? { background: 'transparent', border: '1px solid var(--hairline)' }
              : { background: 'var(--hairline)' }
            }
          />
        );
      })}
    </span>
  );
}

/* Two targets, like a task row: the ring logs a rep straight away (with the
   pop and a burst of sparks — the celebration is the point), the rest of the
   tile opens the sheet. A done tile wears a wash of its hue that fades to
   white across the card, and its ring tile glows. */
export function HabitTile({ habit, pinned, onOpen, onCheckIn, flash, weekDays, checkins, index = 0 }: {
  habit: Habit; pinned?: boolean; onOpen: () => void; onCheckIn?: () => void; flash?: boolean;
  /** the seven days to draw under the name; omit for no dots */
  weekDays?: string[]; checkins?: Record<string, DayCheckin>; index?: number;
}) {
  const pct = habit.target > 1 ? habit.progress / habit.target : habit.done ? 1 : 0;
  const [burst, setBurst] = useState(0);
  const checkIn = () => {
    if (!habit.done) setBurst((n) => n + 1);
    onCheckIn?.();
  };
  const t = hueVar(habit.hue, 't');
  const c = hueVar(habit.hue, 'c');
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpen()}
      aria-label={`Open actions for ${habit.name}`}
      className={`group/tile rise-in surface surface-hover relative flex h-full w-full min-w-0 cursor-pointer flex-col items-center gap-2 px-3 pb-3.5 pt-4 text-center hover:-translate-y-[2px] active:translate-y-0 ${burst && habit.done ? 'tile-shine' : ''}`}
      style={{
        ['--i' as string]: index,
        ['--shine' as string]: `color-mix(in srgb, ${c} 35%, transparent)`,
        ...(habit.done
          ? { background: `linear-gradient(165deg, ${t} 0%, var(--card) 85%)`, borderColor: `color-mix(in srgb, ${c} 22%, transparent)` }
          : null),
      }}
    >
      {/* a wash of the hue at the top edge, only under the pointer */}
      <span className="pointer-events-none absolute inset-x-0 top-0 h-20 rounded-t-card opacity-0 transition-opacity duration-300 group-hover/tile:opacity-100" style={{ background: `linear-gradient(180deg, color-mix(in srgb, ${c} 10%, transparent), transparent)` }} aria-hidden />
      {flash ? <span className="pinflash pointer-events-none absolute inset-0 rounded-card bg-frog-soft" /> : null}
      {pinned ? <span className="absolute left-2.5 top-2.5"><PinBadge small /></span> : null}
      <HabitBadge habit={habit} className="absolute right-3 top-2.5" />
      <span className="relative">
        {onCheckIn ? (
          <RingButton hue={habit.hue} label={habit.done ? `Undo ${habit.name}` : habit.target > 1 ? `Log one for ${habit.name}` : `Check in ${habit.name}`} onClick={checkIn}>
            <HabitRing hue={habit.hue} pct={pct} emoji={habit.emoji} done={habit.done} />
          </RingButton>
        ) : (
          <HabitRing hue={habit.hue} pct={pct} emoji={habit.emoji} done={habit.done} />
        )}
        {burst ? <Burst key={burst} hue={habit.hue} /> : null}
      </span>
      <span className="line-clamp-2 text-[14.5px] font-semibold leading-[19px] text-ink">{habit.name}</span>
      {weekDays ? <WeekDots habit={habit} days={checkins} weekDays={weekDays} /> : null}
    </div>
  );
}

/* ---------- habit row (the list layout / library) ---------- */
export function HabitRow({ habit, pinned, first, onOpen, grip }: { habit: Habit; pinned?: boolean; first?: boolean; onOpen: () => void; grip?: React.ReactNode }) {
  const pct = habit.target > 1 ? habit.progress / habit.target : habit.done ? 1 : 0;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpen()}
      className={`row-hover flex cursor-pointer items-center gap-3.5 px-4 py-2.5 ${first ? '' : 'border-t border-hairline'}`}
    >
      <HabitRing hue={habit.hue} pct={pct} emoji={habit.emoji} done={habit.done} size={44} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[15px] font-semibold leading-[21px] text-ink">{habit.name}</div>
        <div className="text-[13px] text-meta">
          {habit.done ? 'Done today' : habit.target > 1 ? `${habit.progress} of ${habit.target} today` : habit.period ? `${habit.period.done} of ${habit.period.target} this week` : habit.streak > 0 ? `🔥 ${habit.streak}-${habit.unit} streak` : 'Not yet today'}
        </div>
      </div>
      {pinned ? <PinBadge /> : <HabitBadge habit={habit} />}
      {grip}
    </div>
  );
}

/* ---------- task row ---------- */
export function TaskRow({
  task, first, pinned, hue, projectNote, tagNote, dateNote, dateTone = 'replan', onOpen, onComplete, grip, index,
}: {
  task: Task; first?: boolean; pinned?: boolean; hue: HueName; projectNote?: string; tagNote?: string[];
  dateNote?: string; dateTone?: 'replan' | 'meta'; onOpen: () => void; onComplete: () => void; grip?: React.ReactNode; index?: number;
}) {
  const [completing, setCompleting] = useState(false);
  const complete = () => {
    setCompleting(true);
    setTimeout(() => { onComplete(); setCompleting(false); }, 300);
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpen()}
      className={`group/row row-hover relative flex cursor-pointer items-center gap-3.5 overflow-hidden px-4 py-[11px] ${first ? '' : 'border-t border-hairline'} ${completing ? 'completing sweep' : ''} ${index != null ? 'rise-in' : ''}`}
      style={{ ['--sweep-color' as string]: hueVar(hue, 't'), ...(index != null ? { ['--i' as string]: index } : null) }}
    >
      <RingButton hue={hue} label={`Complete ${task.name}`} onClick={complete}>
        <Ring size={30} stroke={3.5} hue={hue} pct={completing || task.done ? 1 : 0} check />
      </RingButton>
      <div className="min-w-0 flex-1">
        <div className={`truncate text-[15px] font-semibold leading-[21px] transition-colors ${completing ? 'text-tertiary line-through' : 'text-ink'}`}>{task.name}</div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
          {dateNote ? <Note icon="calendar" text={dateNote} tone={dateTone} /> : null}
          {projectNote ? <Note icon="list" text={projectNote} tone="meta" /> : null}
          {tagNote && tagNote.length > 0 ? <Note icon="tag" text={tagNote.join(' · ')} tone="meta" /> : null}
          {task.subSteps.length > 0 ? <Note icon="check-circle" text={`${task.subSteps.filter((s) => s.done).length}/${task.subSteps.length} steps`} tone="meta" /> : null}
        </div>
      </div>
      {task.remindAt != null ? <span className="num rounded-md bg-card-sunk px-1.5 py-0.5 text-[12px] font-bold text-ink-2">{formatTime(task.remindAt)}</span> : null}
      {task.rrule ? <span className="text-tertiary" title="Repeats"><Icon name="clock" size={13} /></span> : null}
      {pinned ? <PinBadge /> : null}
      {grip}
    </div>
  );
}

function Note({ icon, text, tone }: { icon: IconName; text: string; tone: 'replan' | 'meta' }) {
  return (
    <span className={`mt-0.5 flex items-center gap-1 text-[12px] font-semibold ${tone === 'replan' ? 'text-replan' : 'text-meta'}`}>
      <Icon name={icon} size={12} strokeWidth={2.2} />
      <span className="truncate">{text}</span>
    </span>
  );
}

/* ---------- resolved rows ---------- */
export function DoneRow({ task, hue, first, note, onOpen }: { task: Task; hue: HueName; first?: boolean; note?: string; onOpen: () => void }) {
  return (
    <div role="button" tabIndex={0} onClick={onOpen} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpen()}
      className={`flex cursor-pointer items-center gap-3.5 px-4 py-2.5 opacity-60 transition hover:opacity-90 ${first ? '' : 'border-t border-hairline'}`}>
      <Ring size={30} stroke={3.5} hue={hue} pct={1} check />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[15px] font-semibold leading-[21px] text-tertiary line-through decoration-hairline">{task.name}</div>
        {note ? <Note icon="calendar" text={note} tone="meta" /> : null}
      </div>
    </div>
  );
}

export function SkippedRow({ task, hue, first, onOpen }: { task: Task; hue: HueName; first?: boolean; onOpen: () => void }) {
  return (
    <div role="button" tabIndex={0} onClick={onOpen} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpen()}
      className={`flex cursor-pointer items-center gap-3.5 px-4 py-2.5 opacity-60 transition hover:opacity-90 ${first ? '' : 'border-t border-hairline'}`}>
      <span className="flex h-[30px] w-[30px] items-center justify-center rounded-[9px]" style={{ background: hueVar(hue, 't'), color: hueVar(hue, 'k') }}>
        <Icon name="skip-forward" size={14} />
      </span>
      <div className="min-w-0 flex-1 truncate text-[15px] font-semibold leading-[21px] text-tertiary">{task.name}</div>
      <span className="text-[12px] font-semibold text-meta">Skipped</span>
    </div>
  );
}

/* ---------- Plan's drawers and day labels ---------- */
export function SectionDrawer({
  label, sub, icon, tone, count, open, onToggle, children, index,
}: {
  label: string; sub: string; icon: IconName; tone: 'orange' | 'blue'; count: number; open: boolean; onToggle: () => void; children: React.ReactNode; index?: number;
}) {
  const hue: HueName = tone === 'orange' ? 'orange' : 'blue';
  return (
    <CardGroup className="mt-3" index={index}>
      <button type="button" onClick={onToggle} aria-expanded={open} className="row-hover flex w-full items-center gap-3.5 px-4 py-3 text-left">
        <span className="flex h-[32px] w-[32px] items-center justify-center rounded-[10px]" style={{ background: hueVar(hue, 't'), color: hueVar(hue, 'k') }}>
          <Icon name={icon} size={15} strokeWidth={2.2} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[15px] font-semibold leading-[21px] text-ink">{label}</span>
          <span className="block text-[12px] text-meta">{sub}</span>
        </span>
        <span className="num rounded-full px-2.5 py-0.5 text-[13px] font-bold" style={{ background: hueVar(hue, 't'), color: hueVar(hue, 'k') }}>{count}</span>
        <span className={`text-tertiary transition-transform duration-200 ${open ? 'rotate-180' : ''}`}><Icon name="chevron-down" size={18} strokeWidth={2.4} /></span>
      </button>
      {open ? <div className="border-t border-hairline">{children}</div> : null}
    </CardGroup>
  );
}

export function DayLabel({ children, sub }: { children: React.ReactNode; sub?: React.ReactNode }) {
  return (
    <h3 className="mx-1 mb-2.5 mt-7 flex items-baseline gap-2">
      <span className="font-head text-ink">{children}</span>
      {sub ? <span className="text-[12px] font-semibold text-tertiary">{sub}</span> : null}
    </h3>
  );
}

export function Toggle({ label, open, onToggle }: { label: string; open: boolean; onToggle: () => void }) {
  return (
    <button type="button" onClick={onToggle} aria-expanded={open} className="mt-4 flex w-full items-center justify-between px-1 py-2 text-left">
      <span className="text-[11.5px] font-bold uppercase tracking-[1px] text-meta">{label}</span>
      <span className={`text-tertiary transition-transform duration-200 ${open ? 'rotate-90' : ''}`}><Icon name="chevron-right" size={18} /></span>
    </button>
  );
}

export function GhostAdd({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="group/ghost mt-3 flex w-full items-center gap-3 rounded-row border-[1.5px] border-dashed border-hairline px-4 py-4 text-left transition hover:border-frog/40 hover:bg-frog-wash/40">
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-frog-soft text-frog transition group-hover/ghost:scale-110"><Icon name="plus" size={14} strokeWidth={2.6} /></span>
      <span className="text-[14.5px] text-meta">{label}</span>
    </button>
  );
}
